import type { TopicTreeView } from '../services/topics';

type ExamType =
  'NBME' | 'COMLEX' | 'RESIDENCY' | 'PRE_CLINICAL' | 'CUSTOM' | 'NCLEX';

type AccessLevel = 'TRIAL' | 'FREE' | 'PRE_CLINICAL' | 'STEP_SHELF';

export type YieldFilterKey =
  | 'yield'
  | 'step1Yield'
  | 'step2Yield'
  | 'neurologyYield'
  | 'psychiatryYield'
  | 'pediatricsYield'
  | 'surgeryYield'
  | 'obgynYield'
  | 'familyYield'
  | 'emergencyYield'
  | 'internalYield'
  | 'comlex1Yield'
  | 'comlex2Yield';

export type WorkloadTierId =
  'cram' | 'high_yield' | 'standard' | 'advanced' | 'gunner';

export const WORKLOAD_TIER_DESCRIPTIONS: Record<WorkloadTierId, string> = {
  cram: 'Minimal plan for quick review or limited time.',
  high_yield: 'Focused plan covering the most important topics.',
  standard: 'Balanced plan for solid exam preparation.',
  advanced: 'Comprehensive plan for deeper mastery.',
  gunner: 'Maximum plan for complete coverage and top scores.',
};

// Tier names shown to students, keyed by exam type. Medical exams keep the
// labels they have always had; NCLEX swaps the med-school slang ("Cram",
// "Gunner") for neutral names. Each exam's `workloads[].label` below is the
// displayed name, so an exam type's entries take their labels from here.
export const DEFAULT_WORKLOAD_TIER_LABELS: Record<WorkloadTierId, string> = {
  cram: 'Cram',
  high_yield: 'High Yield',
  standard: 'Standard',
  advanced: 'Advanced',
  gunner: 'Gunner',
};

const NCLEX_WORKLOAD_TIER_LABELS: Record<WorkloadTierId, string> = {
  cram: 'Essentials',
  high_yield: 'High-yield',
  standard: 'Standard',
  advanced: 'Extended',
  gunner: 'Complete',
};

export const WORKLOAD_TIER_LABELS_BY_EXAM_TYPE: Partial<
  Record<ExamType, Record<WorkloadTierId, string>>
> = {
  NCLEX: NCLEX_WORKLOAD_TIER_LABELS,
};

export const getWorkloadTierLabel = (
  examType: string | null | undefined,
  tier: WorkloadTierId
): string =>
  WORKLOAD_TIER_LABELS_BY_EXAM_TYPE[examType as ExamType]?.[tier] ??
  DEFAULT_WORKLOAD_TIER_LABELS[tier];

// Pinned in docs/new-domains/nclex-rn/platform-scoping-spec.md §8a.
export const NCLEX_EXAM_ID = 'b2fb3a1c-57cc-5eac-8bb2-d65355c06d8d';

// Topic-tree views of the nursing exam. These are also members of the shared
// `TopicTreeView` enum (`NURSING_TOPIC_TREE_VIEWS` in services/topics).
export type NursingTopicTreeView = 'nursing_subjects' | 'client_needs';

export interface ExamWorkloadItem {
  tier: WorkloadTierId;
  label: string;
  flashcards: number;
  vignettes: number;
  example?: string;
}

/**
 * The question-view chrome of an exam whose interface differs from the medical
 * exams. Mirrors ora-be `EXAM_CONFIG[...].chrome` (served on
 * `GET /v5/topics/exam-config`; contract in ora-be docs/nclex-practice-test.md).
 * Absent on every medical exam: absent means today's medical chrome.
 */
export interface ExamChrome {
  /** false: no Lab Values button or sheet. */
  labValuesSheet: boolean;
  /** true: the on-screen calculator, with its hotkey and keyboard entry. */
  calculator: boolean;
  /** false: no per-question timer in tutor blocks. */
  perItemTimer: boolean;
  /** Practice test only (K7): the server enforces it per block. */
  forwardOnly: boolean;
  /** Practice test only: one clock for the whole test, in seconds. */
  totalClockSeconds: number;
  /**
   * false: no Try Again switch, and every item reveals on the first submit
   * whatever the stored `tryAgainMode` preference says (ORA-952 D1).
   */
  tryAgain: boolean;
}

interface ExamWorkloadConfig {
  name: string;
  // Compact label for tight UI; set only where it differs from `name`.
  shortLabel?: string;
  type: ExamType;
  workloads: ExamWorkloadItem[];
  yieldFilter: YieldFilterKey;
  accessLevel: AccessLevel;
  // Optional, set only where an exam differs from the defaults (mirrors
  // ora-be examConfig): videos on, 90 s per timed QBank question, and the
  // topic views the backend's topic config reports.
  defaultUsingVideos?: boolean;
  timedSecondsPerQuestion?: number;
  chrome?: ExamChrome;
  topicViews?: {
    allowedViews: readonly (TopicTreeView | NursingTopicTreeView)[];
    defaultView: TopicTreeView | NursingTopicTreeView;
  };
}

type ExamWorkloadConfigMap = Record<string, ExamWorkloadConfig>;

export const EXAM_CONFIG: ExamWorkloadConfigMap = {
  '8412aecf-480b-4d6a-ad55-e98b08bc21c8': {
    name: 'USMLE Step 1',
    workloads: [
      {
        tier: 'cram',
        label: 'Cram',
        flashcards: 2000,
        vignettes: 500,
        example: 'Sickle cell valine substitution',
      },
      {
        tier: 'high_yield',
        label: 'High Yield',
        flashcards: 5000,
        vignettes: 1000,
        example: "Warfarin's vitamin K epoxide reductase inhibition",
      },
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 9000,
        vignettes: 2000,
        example: 'Type IV renal tubular acidosis mechanism',
      },
      {
        tier: 'advanced',
        label: 'Advanced',
        flashcards: 15000,
        vignettes: 3000,
        example: 'Fabry disease alpha-galactosidase A deficiency',
      },
      {
        tier: 'gunner',
        label: 'Gunner',
        flashcards: 22000,
        vignettes: 4000,
        example: 'Strychnine glycine receptor antagonism',
      },
    ],
    type: 'NBME',
    yieldFilter: 'step1Yield',
    accessLevel: 'PRE_CLINICAL',
  },
  '88b6758c-3cdb-49e9-8913-5a3553411634': {
    name: 'USMLE Step 2',
    workloads: [
      {
        tier: 'cram',
        label: 'Cram',
        flashcards: 2000,
        vignettes: 500,
        example: 'Magnesium sulfate for eclampsia seizure prophylaxis',
      },
      {
        tier: 'high_yield',
        label: 'High Yield',
        flashcards: 5000,
        vignettes: 1000,
        example: 'Troponin-negative chest pain next step',
      },
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 9000,
        vignettes: 2000,
        example: 'Infliximab tuberculosis screening before initiation',
      },
      {
        tier: 'advanced',
        label: 'Advanced',
        flashcards: 15000,
        vignettes: 3000,
        example: 'Phenoxybenzamine before pheochromocytoma resection',
      },
      {
        tier: 'gunner',
        label: 'Gunner',
        flashcards: 22000,
        vignettes: 4000,
        example: 'Glucagonoma necrolytic migratory erythema',
      },
    ],
    type: 'NBME',
    yieldFilter: 'step2Yield',
    accessLevel: 'STEP_SHELF',
  },
  '0eb1b6b1-e1a0-4400-8c68-75d4236df57a': {
    name: 'Neurology Shelf Exam',
    workloads: [
      {
        tier: 'cram',
        label: 'Cram',
        flashcards: 150,
        vignettes: 60,
        example: 'MCA stroke contralateral findings',
      },
      {
        tier: 'high_yield',
        label: 'High Yield',
        flashcards: 400,
        vignettes: 150,
        example: 'Oligoclonal bands in cerebrospinal fluid',
      },
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 700,
        vignettes: 250,
        example: 'Nerve conduction velocity in demyelinating neuropathy',
      },
      {
        tier: 'advanced',
        label: 'Advanced',
        flashcards: 1000,
        vignettes: 370,
        example: 'Anti-Hu antibody with small cell lung cancer',
      },
      {
        tier: 'gunner',
        label: 'Gunner',
        flashcards: 1400,
        vignettes: 500,
        example: 'Ovarian teratoma triggering anti-NMDA receptor encephalitis',
      },
    ],
    type: 'NBME',
    yieldFilter: 'neurologyYield',
    accessLevel: 'STEP_SHELF',
  },
  '47beb60a-ff39-4768-bc85-9488b714bc70': {
    name: 'Psychiatry Shelf Exam',
    workloads: [
      {
        tier: 'cram',
        label: 'Cram',
        flashcards: 120,
        vignettes: 60,
        example: 'Sertraline for first-episode depression',
      },
      {
        tier: 'high_yield',
        label: 'High Yield',
        flashcards: 350,
        vignettes: 150,
        example: 'Six-month duration requirement for schizophrenia',
      },
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 600,
        vignettes: 250,
        example: 'Borderline personality and splitting defense mechanism',
      },
      {
        tier: 'advanced',
        label: 'Advanced',
        flashcards: 850,
        vignettes: 370,
        example: 'Dantrolene for neuroleptic malignant syndrome rigidity',
      },
      {
        tier: 'gunner',
        label: 'Gunner',
        flashcards: 1200,
        vignettes: 500,
        example: 'Capgras delusion impostor replacement belief',
      },
    ],
    type: 'NBME',
    yieldFilter: 'psychiatryYield',
    accessLevel: 'STEP_SHELF',
  },
  '8b790ec9-5452-484e-bd9c-3b73bb003466': {
    name: 'Surgery Shelf Exam',
    workloads: [
      {
        tier: 'cram',
        label: 'Cram',
        flashcards: 400,
        vignettes: 200,
        example: "McBurney's point tenderness next step",
      },
      {
        tier: 'high_yield',
        label: 'High Yield',
        flashcards: 700,
        vignettes: 300,
        example: 'Solitary thyroid nodule fine-needle aspiration criteria',
      },
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 1000,
        vignettes: 400,
        example: 'Post-operative day 3 fever and atelectasis',
      },
      {
        tier: 'advanced',
        label: 'Advanced',
        flashcards: 1300,
        vignettes: 500,
        example: 'Lethal triad in trauma resuscitation',
      },
      {
        tier: 'gunner',
        label: 'Gunner',
        flashcards: 1800,
        vignettes: 600,
        example: 'Superior mesenteric artery syndrome after spica cast',
      },
    ],
    type: 'NBME',
    yieldFilter: 'surgeryYield',
    accessLevel: 'STEP_SHELF',
  },
  'e3cbf8d9-545b-4ed3-925a-1ade1f013e2a': {
    name: 'Family Medicine Shelf Exam',
    workloads: [
      {
        tier: 'cram',
        label: 'Cram',
        flashcards: 200,
        vignettes: 80,
        example: 'Colonoscopy screening starting at age 45',
      },
      {
        tier: 'high_yield',
        label: 'High Yield',
        flashcards: 500,
        vignettes: 200,
        example: 'Metformin as first-line for type 2 diabetes',
      },
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 1000,
        vignettes: 400,
        example: 'Bisphosphonate indications by T-score',
      },
      {
        tier: 'advanced',
        label: 'Advanced',
        flashcards: 1400,
        vignettes: 550,
        example: 'Eggshell calcifications on chest X-ray in silicosis',
      },
      {
        tier: 'gunner',
        label: 'Gunner',
        flashcards: 1900,
        vignettes: 750,
        example: 'Proctalgia fugax fleeting rectal spasm at night',
      },
    ],
    type: 'NBME',
    yieldFilter: 'familyYield',
    accessLevel: 'STEP_SHELF',
  },
  'f10b1aa0-dd6d-4376-9bbd-3a1f25500a2e': {
    name: 'OBGYN Shelf Exam',
    workloads: [
      {
        tier: 'cram',
        label: 'Cram',
        flashcards: 200,
        vignettes: 200,
        example: 'McRoberts maneuver for shoulder dystocia',
      },
      {
        tier: 'high_yield',
        label: 'High Yield',
        flashcards: 500,
        vignettes: 400,
        example: 'Metformin for polycystic ovary syndrome',
      },
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 1000,
        vignettes: 500,
        example: 'Endometrial biopsy for postmenopausal bleeding',
      },
      {
        tier: 'advanced',
        label: 'Advanced',
        flashcards: 1500,
        vignettes: 600,
        example: 'Complete molar pregnancy 46,XX karyotype',
      },
      {
        tier: 'gunner',
        label: 'Gunner',
        flashcards: 2000,
        vignettes: 700,
        example: 'Asherman syndrome intrauterine adhesions on hysteroscopy',
      },
    ],
    type: 'NBME',
    yieldFilter: 'obgynYield',
    accessLevel: 'STEP_SHELF',
  },
  '7e18060e-5066-4afa-9b40-d83f98dd905e': {
    name: 'Emergency Medicine Shelf Exam',
    workloads: [
      {
        tier: 'cram',
        label: 'Cram',
        flashcards: 180,
        vignettes: 60,
        example: 'Epinephrine in pulseless ventricular tachycardia',
      },
      {
        tier: 'high_yield',
        label: 'High Yield',
        flashcards: 450,
        vignettes: 150,
        example: 'Physostigmine for anticholinergic toxicity',
      },
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 850,
        vignettes: 250,
        example: 'Emergent bedside thoracotomy indications',
      },
      {
        tier: 'advanced',
        label: 'Advanced',
        flashcards: 1200,
        vignettes: 370,
        example: 'Crotalidae antivenom for pit viper envenomation',
      },
      {
        tier: 'gunner',
        label: 'Gunner',
        flashcards: 1700,
        vignettes: 500,
        example: 'Tympanic membrane rupture from blast wave',
      },
    ],
    type: 'NBME',
    yieldFilter: 'emergencyYield',
    accessLevel: 'STEP_SHELF',
  },
  '58e0559f-d98f-4747-984d-caa6d2829761': {
    name: 'Internal Medicine Shelf Exam',
    workloads: [
      {
        tier: 'cram',
        label: 'Cram',
        flashcards: 500,
        vignettes: 200,
        example: 'Normal saline bolus in diabetic ketoacidosis',
      },
      {
        tier: 'high_yield',
        label: 'High Yield',
        flashcards: 1500,
        vignettes: 400,
        example: 'Isolated anti-HBc antibody interpretation',
      },
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 2500,
        vignettes: 600,
        example: 'Renal artery stenosis bruit in young woman',
      },
      {
        tier: 'advanced',
        label: 'Advanced',
        flashcards: 3500,
        vignettes: 800,
        example: 'Cryoglobulinemia palpable purpura with hepatitis C',
      },
      {
        tier: 'gunner',
        label: 'Gunner',
        flashcards: 4500,
        vignettes: 1000,
        example: 'Whipple disease PAS-positive foamy macrophages',
      },
    ],
    type: 'NBME',
    yieldFilter: 'internalYield',
    accessLevel: 'STEP_SHELF',
  },
  '67250d46-779b-4208-9b4c-e25e8005ba86': {
    name: 'Pediatrics Shelf Exam',
    workloads: [
      {
        tier: 'cram',
        label: 'Cram',
        flashcards: 200,
        vignettes: 80,
        example: 'Milestone red flags at 2 years',
      },
      {
        tier: 'high_yield',
        label: 'High Yield',
        flashcards: 500,
        vignettes: 200,
        example: 'Kawasaki coronary artery aneurysm prevention',
      },
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 1000,
        vignettes: 350,
        example: 'Tetralogy of Fallot tet spell management',
      },
      {
        tier: 'advanced',
        label: 'Advanced',
        flashcards: 1500,
        vignettes: 500,
        example: 'Maple syrup urine disease branched-chain amino acids',
      },
      {
        tier: 'gunner',
        label: 'Gunner',
        flashcards: 2000,
        vignettes: 700,
        example: 'Beckwith-Wiedemann hemihyperplasia tumor screening',
      },
    ],
    type: 'NBME',
    yieldFilter: 'pediatricsYield',
    accessLevel: 'STEP_SHELF',
  },
  'f0f5f861-bfc0-4575-b1d1-da381471dda5': {
    name: 'COMLEX 1',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 500, vignettes: 250 },
    ],
    type: 'COMLEX',
    yieldFilter: 'comlex1Yield',
    accessLevel: 'PRE_CLINICAL',
  },
  '69c7ee7d-3f9c-42e8-8fd6-97e2c7e130ad': {
    name: 'COMLEX 2',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 500, vignettes: 250 },
    ],
    type: 'COMLEX',
    yieldFilter: 'comlex2Yield',
    accessLevel: 'STEP_SHELF',
  },
  '3ef9eadb-1161-427b-9a02-3accf46f4010': {
    name: 'USMLE Step 3',
    workloads: [
      {
        tier: 'cram',
        label: 'Cram',
        flashcards: 6204,
        vignettes: 473,
        example: 'Oxytocin for uterine atony after delivery',
      },
      {
        tier: 'high_yield',
        label: 'High Yield',
        flashcards: 10000,
        vignettes: 949,
        example: 'Empiric antibiotics for febrile neutropenia',
      },
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 13891,
        vignettes: 1906,
        example: 'Hormonal evaluation of an adrenal incidentaloma',
      },
      {
        tier: 'advanced',
        label: 'Advanced',
        flashcards: 15891,
        vignettes: 2861,
        example: 'Cerclage criteria for cervical insufficiency',
      },
      {
        tier: 'gunner',
        label: 'Gunner',
        flashcards: 17566,
        vignettes: 4252,
        example: 'Interpreting a supervised fast for insulinoma',
      },
    ],
    type: 'NBME',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '2baddb10-faa3-4c4c-a9b9-137c9a5f8a22': {
    name: 'COMLEX 3',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 0, vignettes: 220 },
    ],
    type: 'COMLEX',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  'cbc21ed0-bc7f-4747-8244-cc02fe48d77b': {
    name: 'Anesthesiology',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 543, vignettes: 100 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '7b4c559c-1b72-4eb9-b128-03e0b697f76b': {
    name: 'Child Neurology',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 904, vignettes: 100 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  'e2824a2e-ec9a-403a-a33f-feae017b0da5': {
    name: 'Dermatology',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 769, vignettes: 100 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '93090bc0-2405-4dc2-b7ee-e93596afc8b0': {
    name: 'Diagnostic Radiology',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 814, vignettes: 98 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  'cdcef4f8-6371-4f0a-bbb2-98336afa586b': {
    name: 'Emergency Medicine',
    workloads: [
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 1120,
        vignettes: 98,
      },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  'f67f2ed7-f624-4d77-8940-d1e535b5cc17': {
    name: 'Family Medicine',
    workloads: [
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 1175,
        vignettes: 100,
      },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '4fd8d2d7-2918-407e-9f46-b4db117986e4': {
    name: 'General Surgery',
    workloads: [
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 1085,
        vignettes: 99,
      },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  'dc50dd62-3f3c-43fc-9136-50422f6ac699': {
    name: 'Internal Medicine',
    workloads: [
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 1618,
        vignettes: 100,
      },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '79f37177-8501-48f7-a205-4b648f8d86ba': {
    name: 'Interventional Radiology',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 385, vignettes: 96 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '8e5b5ce3-00c5-4adc-a390-d240c93da002': {
    name: 'Med-Peds',
    workloads: [
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 1164,
        vignettes: 99,
      },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '251ff459-28a1-4d54-ad43-174e734f07bc': {
    name: 'Neurology',
    workloads: [
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 1105,
        vignettes: 99,
      },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  'e00f38be-eb2a-4706-b06b-734af0a8525e': {
    name: 'Neurosurgery',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 805, vignettes: 100 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '87a11362-b29e-4131-9e56-64c8a9478579': {
    name: 'Obstetrics & Gynecology',
    workloads: [
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 1236,
        vignettes: 99,
      },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  'cba537b0-0496-42ae-8e8b-9deebce1b466': {
    name: 'Ophthalmology',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 568, vignettes: 100 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  'd9c9bd68-8118-4fde-aa51-2eb2a2a4e3c8': {
    name: 'Orthopedic Surgery',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 644, vignettes: 100 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '5b75e347-05a0-4d3f-842d-fbb556f05dc7': {
    name: 'Otolaryngology',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 644, vignettes: 100 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '8865fdec-fb47-4b47-9ed7-45a129ae2b24': {
    name: 'Pathology',
    workloads: [
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 1142,
        vignettes: 99,
      },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '23f93de3-8347-4d2a-ba12-e5d6a2124b4b': {
    name: 'Pediatrics',
    workloads: [
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 1403,
        vignettes: 100,
      },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '25df94ed-68d2-4471-9ba6-e75faef3c1d3': {
    name: 'Physical Medicine and Rehabilitation',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 634, vignettes: 96 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  'ae3b181d-535c-48a3-9ffd-670c06ec3c33': {
    name: 'Plastic Surgery',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 299, vignettes: 99 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  'eeb79c9c-34a4-476e-8f2f-aadd6aee4768': {
    name: 'Psychiatry',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 835, vignettes: 97 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  'b4055e53-13b0-41f9-b1bf-cdc241a0084e': {
    name: 'Radiation Oncology',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 165, vignettes: 73 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '2f70cc49-3e1e-4c80-b293-e3ec4e5cd3a3': {
    name: 'Thoracic Surgery',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 767, vignettes: 100 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '877bb8d0-53bb-492c-b0e9-0366c1edaad4': {
    name: 'Urology',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 852, vignettes: 99 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '367b947a-46a0-4feb-af17-9372ced95cca': {
    name: 'Vascular Surgery',
    workloads: [
      { tier: 'standard', label: 'Standard', flashcards: 456, vignettes: 98 },
    ],
    type: 'RESIDENCY',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
  },
  '7635c078-433a-4063-aa76-f19cddcfaea3': {
    name: 'Preclinical',
    workloads: [
      {
        tier: 'cram',
        label: 'Cram',
        flashcards: 2000,
        vignettes: 500,
        example: 'Sickle cell valine substitution',
      },
      {
        tier: 'high_yield',
        label: 'High Yield',
        flashcards: 5000,
        vignettes: 1000,
        example: "Warfarin's vitamin K epoxide reductase inhibition",
      },
      {
        tier: 'standard',
        label: 'Standard',
        flashcards: 9000,
        vignettes: 2000,
        example: 'Type IV renal tubular acidosis mechanism',
      },
      {
        tier: 'advanced',
        label: 'Advanced',
        flashcards: 15000,
        vignettes: 3000,
        example: 'Fabry disease alpha-galactosidase A deficiency',
      },
      {
        tier: 'gunner',
        label: 'Gunner',
        flashcards: 22000,
        vignettes: 4000,
        example: 'Strychnine glycine receptor antagonism',
      },
    ],
    type: 'PRE_CLINICAL',
    yieldFilter: 'step1Yield',
    accessLevel: 'PRE_CLINICAL',
  },
  // NCLEX-RN (nursing). Inert until the backend sends this exam: it reaches
  // the client only when NCLEX_ENABLED (or the allowlist) is on. Tier sizes
  // mirror the backend's NCLEX tier quotas (ora-be #949): cumulative
  // questions and flashcards per tier. The per-tier "Includes topics like"
  // examples are left out until the content team writes nursing ones.
  [NCLEX_EXAM_ID]: {
    name: 'NCLEX-RN',
    shortLabel: 'NCLEX',
    workloads: [
      {
        tier: 'cram',
        label: getWorkloadTierLabel('NCLEX', 'cram'),
        flashcards: 800,
        vignettes: 300,
      },
      {
        tier: 'high_yield',
        label: getWorkloadTierLabel('NCLEX', 'high_yield'),
        flashcards: 2000,
        vignettes: 800,
      },
      {
        tier: 'standard',
        label: getWorkloadTierLabel('NCLEX', 'standard'),
        flashcards: 3600,
        vignettes: 1400,
      },
      {
        tier: 'advanced',
        label: getWorkloadTierLabel('NCLEX', 'advanced'),
        flashcards: 4400,
        vignettes: 1750,
      },
      {
        tier: 'gunner',
        label: getWorkloadTierLabel('NCLEX', 'gunner'),
        flashcards: 5100,
        vignettes: 2000,
      },
    ],
    type: 'NCLEX',
    yieldFilter: 'yield',
    accessLevel: 'STEP_SHELF',
    defaultUsingVideos: false,
    timedSecondsPerQuestion: 120,
    chrome: {
      labValuesSheet: false,
      calculator: true,
      perItemTimer: false,
      forwardOnly: true,
      totalClockSeconds: 18000,
      tryAgain: false,
    },
    topicViews: {
      allowedViews: ['nursing_subjects', 'client_needs'],
      defaultView: 'nursing_subjects',
    },
  },
} as const;

const WORKLOAD_PRIORITY: Record<WorkloadTierId, number> = {
  high_yield: 1,
  standard: 0,
  advanced: 2,
  cram: 1,
  gunner: 3,
};

const getSortedWorkloads = (workloads: ExamWorkloadItem[]) => {
  const sorted = [...workloads];
  return sorted.sort((a, b) => a.flashcards - b.flashcards);
};

export const getExamWorkloadConfig = (examId?: string | null) => {
  if (!examId) return undefined;
  return EXAM_CONFIG[examId];
};

// The exam's chrome, or undefined for the medical default (see ExamChrome).
export const getExamChrome = (examId?: string | null): ExamChrome | undefined =>
  getExamWorkloadConfig(examId)?.chrome;

// Seconds per question of a timed QBank block (K7): medical 90, NCLEX 120.
// Mirrors ora-be TIMED_BLOCK_SECONDS_PER_QUESTION and the exam override.
export const DEFAULT_TIMED_SECONDS_PER_QUESTION = 90;

export const getTimedSecondsPerQuestion = (examId?: string | null): number =>
  getExamWorkloadConfig(examId)?.timedSecondsPerQuestion ??
  DEFAULT_TIMED_SECONDS_PER_QUESTION;

export const getWorkloadTiers = (examId?: string | null) => {
  return getExamWorkloadConfig(examId)?.workloads
    ? getSortedWorkloads([...getExamWorkloadConfig(examId)!.workloads])
    : [];
};

// Workload applied when the user never picked one (both selected counts null):
// High Yield, not the smallest tier. Tiers are sorted ascending, so a plain
// `workloads[0]` fallback silently defaulted everyone to cram — the smallest
// pool — which both under-serves new users and makes the workload goal
// trivially reachable (ORA-36). Exams without a High Yield tier (such as
// single-tier COMLEX and residency exams) keep their first tier. Mirrors ora-be
// examConfig.
export const DEFAULT_WORKLOAD_TIER: WorkloadTierId = 'high_yield';

export const getDefaultWorkload = (
  workloads: ExamWorkloadItem[]
): ExamWorkloadItem | undefined =>
  workloads.find((workload) => workload.tier === DEFAULT_WORKLOAD_TIER) ??
  workloads[0];

const resolveNearestByCount = (
  workloads: ExamWorkloadItem[],
  selectedCount: number | null | undefined,
  field: 'flashcards' | 'vignettes'
) => {
  const validWorkloads = workloads.filter((workload) => workload[field] > 0);
  if (!selectedCount || validWorkloads.length === 0) {
    return null;
  }

  const ranked = [...validWorkloads].sort((a, b) => {
    const diff =
      Math.abs(a[field] - selectedCount) - Math.abs(b[field] - selectedCount);

    if (diff !== 0) return diff;
    return WORKLOAD_PRIORITY[a.tier] - WORKLOAD_PRIORITY[b.tier];
  });

  return ranked[0] ?? null;
};

export const resolveWorkloadTier = (params: {
  examId?: string | null;
  selectedFlashcardCount: number | null;
  selectedVignetteCount: number | null;
}) => {
  const workloads = getWorkloadTiers(params.examId);
  if (!workloads.length) return null;

  const byVignette = resolveNearestByCount(
    workloads,
    params.selectedVignetteCount,
    'vignettes'
  );

  if (byVignette) {
    return byVignette;
  }

  return (
    resolveNearestByCount(
      workloads,
      params.selectedFlashcardCount,
      'flashcards'
    ) ??
    getDefaultWorkload(workloads) ??
    null
  );
};
